import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import Grid from '@mui/material/Grid';
import { Typography, Card, CardMedia, CardContent, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';
import Main from 'layouts/Main';
import Container from 'components/Container';

const StudentCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 12px 30px rgba(255, 255, 255, 0.2)' 
      : '0 12px 30px rgba(0, 0, 0, 0.2)',
  },
  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
  borderRadius: 16,
  overflow: 'visible',
  position: 'relative',
  border: theme.palette.mode === 'dark' 
    ? '2px solid rgba(100, 150, 200, 0.3)' 
    : '2px solid rgba(100, 150, 200, 0.4)',
}));

const ImageWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingTop: '120%',
  overflow: 'visible',
  borderRadius: '12px',
  margin: '12px',
  background: `linear-gradient(135deg, ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(100,150,200,0.05)'} 0%, ${theme.palette.mode === 'dark' ? 'rgba(100,150,200,0.1)' : 'rgba(200,220,255,0.1)'} 100%)`,
  border: theme.palette.mode === 'dark' 
    ? '2px solid rgba(100, 150, 200, 0.4)' 
    : '2px solid rgba(100, 150, 200, 0.3)',
  '& img': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const RankRibbon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '-8px',
  left: '-8px',
  width: '70px',
  height: '90px',
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  '& .ribbon-circle': {
    width: '56px',
    height: '56px',
    background: '#4a69ff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 6px 16px rgba(74, 105, 255, 0.5)',
    border: '4px solid #fff',
    fontWeight: 900,
    fontSize: '1.8rem',
    color: '#fff',
    textShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
    zIndex: 2,
  },
  '& .ribbon-tails': {
    position: 'absolute',
    top: '48px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '50px',
    height: '28px',
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
    zIndex: 1,
  },
  '& .ribbon-tail': {
    width: '16px',
    height: '26px',
    background: '#4a69ff',
    clipPath: 'polygon(0 0, 100% 0, 85% 100%, 15% 100%)',
    boxShadow: '0 4px 8px rgba(74, 105, 255, 0.3)',
  },
}));

const HeaderSection = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
    : 'linear-gradient(135deg, #c5e1ff 0%, #e8d5f2 50%, #d0f0ff 100%)',
  padding: '50px 20px',
  marginBottom: '50px',
  borderRadius: 0,
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 32px rgba(0, 0, 0, 0.6)'
    : '0 8px 32px rgba(0, 0, 0, 0.12)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'dark'
      ? 'radial-gradient(circle at 20% 50%, rgba(100, 150, 200, 0.1) 0%, transparent 50%)'
      : 'radial-gradient(circle at 20% 50%, rgba(100, 150, 200, 0.15) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginBottom: '50px',
  borderRadius: '12px',
  border: theme.palette.mode === 'dark' 
    ? '2px solid rgba(100, 150, 200, 0.3)' 
    : '2px solid rgba(100, 150, 200, 0.4)',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 24px rgba(0, 0, 0, 0.4)'
    : '0 8px 24px rgba(0, 0, 0, 0.1)',
  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? '#2a3f5f' 
      : '#1565c0',
    color: theme.palette.mode === 'dark' 
      ? '#ffffff' 
      : '#ffffff',
    fontWeight: 800,
    fontSize: '0.95rem',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#3a5f7f' : '#1565c0'}`,
    padding: '16px',
    textAlign: 'center',
  },
  '& .MuiTableCell-body': {
    fontSize: '0.9rem',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#3a5f7f' : '#e0e0e0'}`,
    padding: '14px',
    backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fafafa',
    textAlign: 'center',
    color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#1a1a1a',
  },
  '& .MuiTableRow-root:nth-of-type(even) .MuiTableCell-body': {
    backgroundColor: theme.palette.mode === 'dark' ? '#252525' : '#f0f4f8',
  },
}));

const TableTitleBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: '30px',
  marginTop: '40px',
}));

const PerformanceCard = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #1a3a52 0%, #1e2d42 100%)'
    : 'linear-gradient(135deg, #e3f2fd 0%, #e8d5f2 50%, #d0f0ff 100%)',
  padding: '30px 20px',
  borderRadius: '16px',
  border: theme.palette.mode === 'dark' 
    ? '2px solid rgba(100, 150, 200, 0.4)' 
    : '2px solid rgba(100, 150, 200, 0.3)',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 24px rgba(0, 0, 0, 0.4)'
    : '0 8px 24px rgba(0, 0, 0, 0.1)',
  margin: '40px auto',
  maxWidth: '600px',
}));

const Class10Results2026 = () => {
  const theme = useTheme();
  const [config, setConfig] = useState({ base_image_url_2025: '' });

  useEffect(() => {
    fetch('/config.json')
      .then(response => response.json())
      .then(data => setConfig(data))
      .catch(error => console.error('Error fetching config:', error));
  }, []);

  const baseUrl = config.base_image_url_2025;

  const subjectWiseData = [
    { subject: 'ENGLISH', highestScore: 100, noOfStudents: 3, toppers: 'VIHAN HIREMATH, DIYA S BETAGERI, KAUSTUBHAN HARISH' },
    { subject: 'HINDI', highestScore: 97, noOfStudents: 3, toppers: 'MISHA SHARMA, MOHAMMED ZAYAN KHAN, HAARDIK AGARWAL' },
    { subject: 'SANSKRIT', highestScore: 100, noOfStudents: 12, toppers: 'PRADYUMNA P, VIHAN HIREMATH, VISHODHAN KIDAMBI SUDARSHAN, ABHAY SRINIDHI M B, SHRUTHI CHITHAKINDI, GOWRI R, KAUSTUBHAN HARISH, NIKHIL SRINIVASAN, NISHKA PATEL, PRANAV HARISH, ROHITH S VADHIYAR, VIRENDRA YUVRAJSINGH GEHLOT' },
    { subject: 'BASIC MATH', highestScore: 86, noOfStudents: 1, toppers: 'NIHARIKA MANJUNATH' },
    { subject: 'STANDARD MATH', highestScore: 100, noOfStudents: 2, toppers: 'HAARDIK AGARWAL, ANTARA YATISH' },
    { subject: 'SCIENCE', highestScore: 100, noOfStudents: 5, toppers: 'ESHAN M, KIAAN BHULANI, MEER RAHIL HUSSAIN, ANTARA YATISH, HARISHREE T' },
    { subject: 'SOCIAL SCIENCE', highestScore: 100, noOfStudents: 2, toppers: 'KRISHAY MITTAL, VIHAN HIREMATH' },
  ];

  const skillSubjectData = [
    { subject: 'ARTIFICIAL INTELLIGENCE', highestScore: 100, noOfStudents: 29 },
    { subject: 'FINANCIAL MARKETS', highestScore: 100, noOfStudents: 3 },
  ];

  const performanceData = [
    { range: 'Above 90%', percentage: 54.9 },
    { range: '80-90%', percentage: 27.5 },
    { range: '70-80%', percentage: 13.7 },
    { range: '60-70%', percentage: 3.9 },
  ];

  const students = [
    // Row 1 - 3 students
    { id: 1, name: 'VIHAN HIREMATH', percentage: 99.2, skillPercentage: 99, image: `${baseUrl}/home/achievements2026/class10-results-2026/1.webp` },
    { id: 2, name: 'MOHAMMED ZAYAN KHAN', percentage: 98.4, skillPercentage: 98.7, image: `${baseUrl}/home/achievements2026/class10-results-2026/2.webp` },
    { id: 3, name: 'KAUSTUBHAN HARISH', percentage: 98.2, skillPercentage: 98.2, image: `${baseUrl}/home/achievements2026/class10-results-2026/3.webp` },
    // Row 2 - 4 students
    { id: 4, name: 'NIKHIL SRINIVASAN', percentage: 98, skillPercentage: 98.3, image: `${baseUrl}/home/achievements2026/class10-results-2026/4.webp` },
    { id: 5, name: 'HAARDIK AGARWAL', percentage: 97.8, skillPercentage: 98.2, image: `${baseUrl}/home/achievements2026/class10-results-2026/8.webp` },
    { id: 6, name: 'PRANAV HARISH', percentage: 97.6, skillPercentage: 98, image: `${baseUrl}/home/achievements2026/class10-results-2026/5.webp` },
    { id: 7, name: 'ROHITH S VADHIYAR', percentage: 97.4, skillPercentage: 97.7, image: `${baseUrl}/home/achievements2026/class10-results-2026/6.webp` },
    // Row 3 - 3 students
    { id: 8, name: 'BRINDHA K', percentage: 97, skillPercentage: 97.3, image: `${baseUrl}/home/achievements2026/class10-results-2026/7.webp` },
    { id: 9, name: 'ANIRUDH VAIKUND', percentage: 97, skillPercentage: 97.5, image: `${baseUrl}/home/achievements2026/class10-results-2026/9.webp` },
    { id: 10, name: 'VIRENDRA YUVRAJSINGH GEHLOT', percentage: 97, skillPercentage: 97.3, image: `${baseUrl}/home/achievements2026/class10-results-2026/10.webp` },
    // Row 4 - 4 students
    { id: 11, name: 'JASHAN SHARMA', percentage: 96.8, skillPercentage: 97.3, image: `${baseUrl}/home/achievements2026/class10-results-2026/11.webp` },
    { id: 12, name: 'AARAV BASANT', percentage: 96.6, skillPercentage: 97.2, image: `${baseUrl}/home/achievements2026/class10-results-2026/12.webp` },
    { id: 13, name: 'VISHODHAN KIDAMBI SUDARSHAN', percentage: 96.2, skillPercentage: 96.8, image: `${baseUrl}/home/achievements2026/class10-results-2026/13.webp` },
    { id: 14, name: 'LIKITH SHIVAYOGI TIPPANNAVAR', percentage: 96, skillPercentage: 96.5, image: `${baseUrl}/home/achievements2026/class10-results-2026/14.webp` },
    // Row 5 - 4 students
    { id: 15, name: 'NISHKA PATEL', percentage: 96, skillPercentage: 96.5, image: `${baseUrl}/home/achievements2026/class10-results-2026/15.webp` },
    { id: 16, name: 'KRISHAY MITTAL', percentage: 95.8, skillPercentage: 96.5, image: `${baseUrl}/home/achievements2026/class10-results-2026/16.webp` },
    { id: 17, name: 'ABHAY SRINIDHI M B', percentage: 95.8, skillPercentage: 96.3, image: `${baseUrl}/home/achievements2026/class10-results-2026/17.webp` },
    { id: 18, name: 'YASHAS J S', percentage: 95.6, skillPercentage: 96.3, image: `${baseUrl}/home/achievements2026/class10-results-2026/18.webp` },
    // Row 6 - 3 students
    { id: 19, name: 'CHIRANTANI ASH', percentage: 95.4, skillPercentage: 96, image: `${baseUrl}/home/achievements2026/class10-results-2026/24.webp` },
    { id: 20, name: 'CHINMAYA RAO', percentage: 95, skillPercentage: 95.8, image: `${baseUrl}/home/achievements2026/class10-results-2026/19.webp` },
    { id: 21, name: 'VIDUSHI JAIN', percentage: 95, skillPercentage: 95.7, image: `${baseUrl}/home/achievements2026/class10-results-2026/20.webp` },
    { id: 22, name: 'DYUTI P', percentage: 95, skillPercentage: 95.3, image: `${baseUrl}/home/achievements2026/class10-results-2026/21.webp` },
    // Row 7 - 2 students
    { id: 23, name: 'HARISHREE T', percentage: 95, skillPercentage: 95.7, image: `${baseUrl}/home/achievements2026/class10-results-2026/22.webp` },
    { id: 24, name: 'KHUSHI J RAO', percentage: 95, skillPercentage: 95.8, image: `${baseUrl}/home/achievements2026/class10-results-2026/23.webp` },
  ];

  const rowConfig = [
    { cols: 3, students: students.slice(0, 3) },
    { cols: 4, students: students.slice(3, 7) },
    { cols: 3, students: students.slice(7, 10) },
    { cols: 4, students: students.slice(10, 14) },
    { cols: 4, students: students.slice(14, 18) },
    { cols: 4, students: students.slice(18, 22) },
    { cols: 2, students: students.slice(22, 24) },
  ];

  return (
    <Main>
      <Helmet>
        <title>NPS YPR CBSE Result 2026 Class 10</title>
      </Helmet>
        <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.mode === 'dark' ? '#0a0a0a' : '#fafafa' }}>
      {/* Header Section */}
      <HeaderSection elevation={0}>
        <Box sx={{ textAlign: 'center', mb: 2, position: 'relative', zIndex: 1 }}>
        
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              color: theme.palette.primary.main,
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.4rem' },
              textShadow: theme.palette.mode === 'dark' ? '0 2px 4px rgba(0,0,0,0.3)' : 'none'
            }}
          >
            CBSE Results 2026 - Class 10
          </Typography>
         
          <Typography 
            variant="h6" 
            align={'justify'}
          >
            Student of NPS YPR - Class 10 have delivered an exceptional performance in the CBSE Board Examinations 2025-26, setting new benchmarks of excellence and surpassing all previous records. This outstanding achievement is a reflection of the collective efforts of our students, teachers, and supportive parents. 
          </Typography>
        </Box>
      </HeaderSection>

      {/* Students Grid - School Toppers */}
      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 6 }, pb: 8 }}>
        <TableTitleBox sx={{ mt: 0 }}>
           <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800, 
              color: theme.palette.primary.main,
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' }
            }}
          >
            SSE : 2026 - School Average - 87%
          </Typography>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800, 
              color: theme.palette.primary.main,
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' }
            }}
          >
            School Toppers
          </Typography>
        </TableTitleBox>
        {rowConfig.map((row, rowIndex) => (
          <Grid 
            container 
            spacing={{ xs: 1.5, sm: 2, md: 3, lg: 4 }} 
            sx={{ 
              mb: 5, 
              justifyContent: 'center',
              display: 'flex',
              width: '100%'
            }}
            key={rowIndex}
          >
            {row.students.map((student) => (
              <Grid 
                item 
                xs={row.cols === 2 ? 12 : row.cols === 3 ? 12 : row.cols === 4 ? 6 : 12}
                sm={row.cols === 2 ? 6 : row.cols === 3 ? 4 : 3}
                md={12 / row.cols}
                key={student.id}
                sx={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start'
                }}
              >
                <StudentCard sx={{ width: '100%', maxWidth: { xs: '180px', sm: '200px', md: '220px', lg: '240px' } }}>
                  {student.id === 1 && (
                    <RankRibbon>
                      <Box className="ribbon-circle">{student.id}</Box>
                      <Box className="ribbon-tails">
                        <Box className="ribbon-tail" />
                        <Box className="ribbon-tail" />
                      </Box>
                    </RankRibbon>
                  )}
                  <ImageWrapper>
                    <img
                      src={student.image}
                      alt={student.name}
                      loading="lazy"
                    />
                  </ImageWrapper>
                  <CardContent sx={{ pb: 2.5, pt: 2, px: 1.5, textAlign: 'center' }}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 800, 
                        mb: 1.5,
                        fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' },
                        color: theme.palette.mode === 'dark' ? '#fff' : '#1a1a2e',
                        lineHeight: 1.2,
                        letterSpacing: '0.5px',
                      }}
                    >
                      {student.name}
                    </Typography>
                    <Box sx={{ mb: 1.5 }}>
                      {/* <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.6rem', 
                          color: theme.palette.mode === 'dark' ? '#aaa' : '#666',
                          mb: 0.3,
                          fontWeight: 600,
                          display: 'block'
                        }}
                      >
                        Percentage
                      </Typography> */}
                      <Typography 
                        sx={{ 
                          fontWeight: 800, 
                          color: theme.palette.primary.main,
                          fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1.1rem' }
                        }}
                      >
                        {student.percentage}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.6rem', 
                          color: theme.palette.mode === 'dark' ? '#aaa' : '#666',
                          fontWeight: 600,
                        }}
                      >
                        With Skill Subject
                      </Typography>
                      <Typography 
                        sx={{ 
                          fontWeight: 800, 
                          color: '#030303',
                          fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' }
                        }}>
                        {student.skillPercentage}%
                      </Typography>
                    </Box>
                  </CardContent>
                </StudentCard>
              </Grid>
            ))}
          </Grid>
        ))}
      </Box>

      {/* Subject Wise Highest Score Table */}
      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 6 } }}>
        <TableTitleBox>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800, 
              color: theme.palette.primary.main,
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' }
            }}
          >
            Subject Wise Highest Score
          </Typography>
        </TableTitleBox>

        <StyledTableContainer>
          <StyledTable>
            <TableHead>
              <TableRow>
                <TableCell>SUBJECT</TableCell>
                <TableCell>HIGHEST SCORE</TableCell>
                <TableCell>NO. OF STUDENTS</TableCell>
                <TableCell>NAME OF THE TOPPERS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subjectWiseData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.subject}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main }}>{row.highestScore}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.noOfStudents}</TableCell>
                  <TableCell sx={{ fontSize: '0.85rem', textAlign: 'left' }}>{row.toppers}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        {/* Skill Subject Highest Score Table */}
        <TableTitleBox>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800, 
              color: theme.palette.primary.main,
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' },
              mt: 5
            }}
          >
            Skill Subject Highest Score
          </Typography>
        </TableTitleBox>

        <StyledTableContainer>
          <StyledTable>
            <TableHead>
              <TableRow>
                <TableCell>SUBJECT</TableCell>
                <TableCell>HIGHEST SCORE</TableCell>
                <TableCell>NO. OF STUDENTS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {skillSubjectData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.subject}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main }}>{row.highestScore}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.noOfStudents}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        {/* Overall Performance Section */}
        <TableTitleBox sx={{ mt: 6 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800, 
              color: theme.palette.primary.main,
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' }
            }}
          >
            Overall Performance
          </Typography>
        </TableTitleBox>

        <PerformanceCard elevation={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {performanceData.map((item, index) => (
              <Box 
                key={index}
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingBottom: index < performanceData.length - 1 ? '15px' : '0',
                  borderBottom: index < performanceData.length - 1 ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(100, 150, 200, 0.2)' : 'rgba(100, 150, 200, 0.3)'}` : 'none'
                }}
              >
                <Typography 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                    color: theme.palette.mode === 'dark' ? '#e0e0e0' : '#1a1a1a',
                  }}
                >
                  {item.percentage}% of students scored {item.range}
                </Typography>
              </Box>
            ))}
          </Box>
        </PerformanceCard>
      </Box>
     </Box>
    </Main>
  );
};

export default Class10Results2026